import { DecimalPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { BillingPlan, KeywordTrackerQuota, Marketplace } from "@front/m19-api-client";
import { OrganizationAccountGroupService } from "@front/m19-services";
import { ITableImports, MODAL_DATA } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { CsvExportService, fieldExtractor } from "@m19-board/services/csv-export.service";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { DateTime } from "luxon";

export interface TrackedKeywordsDetailModalData {
  organizationId: number;
  billingPlan: BillingPlan;
  locale: string;
  keywordQuotas: KeywordTrackerQuota[];
}

export interface TrackedKeywordsRow {
  accountName: string;
  marketplace: Marketplace;
  trackedKeywords: number;
}

@Component({
  templateUrl: "./tracked-keywords-detail-modal.component.html",
  imports: [TranslocoDirective, DecimalPipe, ITableImports, ExportButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrackedKeywordsDetailModalComponent {
  private readonly organizationAccountGroupService = inject(OrganizationAccountGroupService);
  private readonly translocoService = inject(TranslocoService);
  private readonly csvExportService = inject(CsvExportService);
  readonly data = inject<TrackedKeywordsDetailModalData>(MODAL_DATA);

  private readonly organizations = toSignal(this.organizationAccountGroupService.allOrganizationAccountGroups$, {
    requireSync: true,
  });

  private readonly accountNames = computed(() => {
    const organization = this.organizations()?.find((o) => o.id === this.data.organizationId);
    return new Map(
      (organization?.accountGroups ?? [])
        .flatMap((group) => group.resources)
        .map((resource) => [`${resource.accountId}_${resource.marketplace}`, resource.accountName]),
    );
  });

  readonly rows = computed<TrackedKeywordsRow[]>(() => {
    const rowsByAccountMarketplace = new Map<string, TrackedKeywordsRow>();
    for (const quota of this.data.keywordQuotas) {
      const key = `${quota.accountId}_${quota.marketplace}`;
      const row = rowsByAccountMarketplace.get(key) ?? {
        accountName: this.accountNames().get(key) || quota.accountId!,
        marketplace: quota.marketplace!,
        trackedKeywords: 0,
      };
      row.trackedKeywords += quota.keywordNumber ?? 0;
      rowsByAccountMarketplace.set(key, row);
    }
    return Array.from(rowsByAccountMarketplace.values())
      .filter((row) => row.trackedKeywords > 0)
      .sort((a, b) => b.trackedKeywords - a.trackedKeywords);
  });

  readonly total = computed(() => this.rows().reduce((total, row) => total + row.trackedKeywords, 0));
  readonly limit =
    (this.data.billingPlan.kwTrackingDailyLimit ?? 0) + (this.data.billingPlan.kwTrackingHourlyLimit ?? 0);

  exportCsv(): void {
    const t = (key: string) => this.translocoService.translate(key);
    this.csvExportService.exportCsv<TrackedKeywordsRow>(
      { prefix: "tracked_keywords_" + DateTime.local().toISODate() },
      this.rows(),
      [
        fieldExtractor(t("common.account"), (row) => row.accountName),
        fieldExtractor(t("common.marketplace"), (row) => row.marketplace),
        fieldExtractor(t("common.keywords"), (row) => `${row.trackedKeywords}`),
      ],
    );
  }
}
